<template>
  <div class="kb-page">
    <AppHeader />

    <!-- Hero -->
    <section class="kb-blogs-hero">
      <div class="kb-blogs-hero__overlay"></div>
      <div class="container-xl kb-blogs-hero__content" data-aos="fade-up">
        <div class="kb-page-hero__breadcrumb">
          <router-link to="/">Home</router-link>
          <i class="bi bi-chevron-right mx-2"></i>
          <span>Blog</span>
        </div>
        <h1>Knowledge Hub</h1>
        <p>Expert insights on tarpaulins, nets, ropes, and industrial protection from Team KeBee</p>
      </div>
    </section>

    <!-- Filters -->
    <section class="kb-filter-bar">
      <div class="container-xl">
        <div class="kb-filters">
          <button
            v-for="cat in categories"
            :key="cat"
            class="kb-filter-btn"
            :class="{ active: activeCategory === cat }"
            @click="activeCategory = cat"
          >{{ cat }}</button>
        </div>
      </div>
    </section>

    <!-- Blog Grid -->
    <section class="kb-section kb-section--light">
      <div class="container-xl">
        <div class="kb-blogs-grid">
          <div
            v-for="(post, idx) in filteredBlogs"
            :key="post.id"
            class="kb-blog-full-card"
            :class="{ 'kb-blog-full-card--featured': idx === 0 && activeCategory === 'All' }"
            data-aos="fade-up"
            :data-aos-delay="(idx % 3) * 80"
            style="cursor:pointer"
            @click="$router.push(`/blog/${post.slug}`)"
          >
            <div class="kb-blog-full-card__img">
              <img :src="post.coverImage" :alt="post.title" loading="lazy" />
              <span class="kb-blog-full-card__cat" :style="{ background: post.categoryColor }">{{ post.category }}</span>
            </div>
            <div class="kb-blog-full-card__body">
              <div class="kb-blog-full-card__meta">
                <span><i class="bi bi-person-circle me-1"></i>{{ post.author }}</span>
                <span><i class="bi bi-calendar3 me-1"></i>{{ post.date }}</span>
                <span><i class="bi bi-clock me-1"></i>{{ post.readTime }}</span>
              </div>
              <h3>{{ post.title }}</h3>
              <p>{{ post.excerpt }}</p>
              <div class="kb-blog-full-card__tags">
                <span v-for="tag in post.tags" :key="tag" class="kb-tag">#{{ tag }}</span>
              </div>
              <router-link :to="`/blog/${post.slug}`" class="kb-btn kb-btn--navy kb-btn--sm mt-3">
                Read Full Article <i class="bi bi-arrow-right ms-1"></i>
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- CTA -->
    <section class="kb-cta-banner">
      <div class="container-xl">
        <div class="kb-cta-banner__content" data-aos="fade-up">
          <h2>Ready to Experience the KeBee Difference?</h2>
          <p>Browse our full product range and request a custom quote today.</p>
          <div class="kb-cta-banner__actions">
            <router-link to="/products" class="kb-btn kb-btn--primary">
              <i class="bi bi-grid-3x3-gap me-2"></i>Browse Products
            </router-link>
            <a href="https://wa.me/919892310123" target="_blank" class="kb-btn kb-btn--outline-white">
              <i class="bi bi-whatsapp me-2"></i>Chat with Us
            </a>
          </div>
        </div>
      </div>
    </section>

    <AppFooter />
  </div>
</template>

<script>
import AppHeader from '@/components/AppHeader.vue'
import AppFooter from '@/components/AppFooter.vue'
import { setSeo } from '@/utils/seo.js'
import { blogs } from '@/data/blogs.js'

export default {
  name: 'BlogsPage',
  components: { AppHeader, AppFooter },
  mounted() {
    setSeo({
      title: 'Knowledge Hub – Tarpaulin & Industrial Protection Blog | KeBee Industries',
      description: 'Expert articles on choosing tarpaulins, agro shade nets, fire-retardant covers, monsoon storage, nylon ropes and more from Team KeBee Industries, Mumbai.',
      path: '/blog',
      keywords: 'tarpaulin blog, agro shade net guide, fire retardant tarpaulin, nylon rope buying guide, KeBee Industries blog'
    })
  },
  data() {
    return {
      blogs,
      activeCategory: 'All',
    }
  },
  computed: {
    categories() {
      return ['All', ...new Set(this.blogs.map(b => b.category))]
    },
    filteredBlogs() {
      if (this.activeCategory === 'All') return this.blogs
      return this.blogs.filter(b => b.category === this.activeCategory)
    }
  }
}
</script>

<style scoped>
/* Hero */
.kb-blogs-hero {
  min-height: 380px;
  background: linear-gradient(135deg, #0F1F3D 0%, #1E3A6E 100%);
  position: relative;
  display: flex;
  align-items: flex-end;
  padding-bottom: 60px;
}
.kb-blogs-hero__overlay {
  position: absolute;
  inset: 0;
  background: url('@/assets/slider3.webp') center/cover no-repeat;
  opacity: 0.12;
}
.kb-blogs-hero__content {
  position: relative;
  z-index: 2;
  padding-top: 120px;
}
.kb-page-hero__breadcrumb {
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  color: rgba(255,255,255,0.6);
  margin-bottom: 1rem;
}
.kb-page-hero__breadcrumb a { color: rgba(255,255,255,0.6); text-decoration: none; }
.kb-page-hero__breadcrumb a:hover { color: #F59E0B; }
.kb-blogs-hero__content h1 {
  font-family: 'Poppins', sans-serif;
  font-size: clamp(2rem, 4vw, 3rem);
  font-weight: 800;
  color: #fff;
  margin-bottom: 0.75rem;
}
.kb-blogs-hero__content p {
  color: rgba(255,255,255,0.72);
  font-size: 1.05rem;
}

/* Filter Bar */
.kb-filter-bar {
  background: #fff;
  border-bottom: 1px solid #E5E7EB;
  position: sticky;
  top: 68px;
  z-index: 50;
}
.kb-filters {
  display: flex;
  gap: 0.5rem;
  padding: 14px 0;
  overflow-x: auto;
}
.kb-filter-btn {
  flex-shrink: 0;
  padding: 8px 20px;
  border-radius: 50px;
  border: 1.5px solid #E5E7EB;
  background: transparent;
  color: #6B7280;
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}
.kb-filter-btn:hover { border-color: #0F1F3D; color: #0F1F3D; }
.kb-filter-btn.active { background: #0F1F3D; border-color: #0F1F3D; color: #fff; }

/* Blog Grid */
.kb-blogs-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
}
.kb-blog-full-card--featured {
  grid-column: span 3;
  display: grid;
  grid-template-columns: 1fr 1fr;
}
.kb-blog-full-card {
  background: #fff;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(15,31,61,0.07);
  transition: transform 0.3s, box-shadow 0.3s;
  display: flex;
  flex-direction: column;
}
.kb-blog-full-card:hover {
  transform: translateY(-5px);
  box-shadow: 0 12px 40px rgba(15,31,61,0.13);
}
.kb-blog-full-card__img {
  position: relative;
  height: 220px;
  overflow: hidden;
}
.kb-blog-full-card--featured .kb-blog-full-card__img {
  height: 100%;
  min-height: 360px;
}
.kb-blog-full-card__img img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s;
}
.kb-blog-full-card:hover .kb-blog-full-card__img img { transform: scale(1.04); }
.kb-blog-full-card__cat {
  position: absolute;
  top: 16px;
  left: 16px;
  color: #fff;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 5px 14px;
  border-radius: 50px;
  text-transform: uppercase;
  letter-spacing: 0.06em;
}
.kb-blog-full-card__body {
  padding: 1.75rem;
  display: flex;
  flex-direction: column;
  flex: 1;
}
.kb-blog-full-card--featured .kb-blog-full-card__body {
  padding: 2.5rem;
  justify-content: center;
}
.kb-blog-full-card__meta {
  display: flex;
  gap: 1rem;
  font-size: 0.78rem;
  color: #9CA3AF;
  margin-bottom: 0.85rem;
  flex-wrap: wrap;
}
.kb-blog-full-card__body h3 {
  font-family: 'Poppins', sans-serif;
  font-size: 1.05rem;
  font-weight: 700;
  color: #0F1F3D;
  margin-bottom: 0.75rem;
  line-height: 1.4;
}
.kb-blog-full-card--featured .kb-blog-full-card__body h3 {
  font-size: 1.4rem;
}
.kb-blog-full-card__body p {
  font-size: 0.875rem;
  color: #6B7280;
  line-height: 1.7;
  flex: 1;
  margin-bottom: 1rem;
}
.kb-blog-full-card__tags {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}
.kb-tag {
  font-size: 0.72rem;
  background: #F3F4F6;
  color: #6B7280;
  padding: 3px 10px;
  border-radius: 50px;
  font-weight: 500;
}

/* CTA Banner */
.kb-cta-banner {
  background: linear-gradient(135deg, #0F1F3D 0%, #1E3A6E 100%);
  padding: 80px 0;
  text-align: center;
}
.kb-cta-banner__content h2 {
  font-family: 'Poppins', sans-serif;
  font-size: clamp(1.5rem, 3vw, 2.25rem);
  font-weight: 700;
  color: #fff;
  margin-bottom: 1rem;
}
.kb-cta-banner__content p {
  color: rgba(255,255,255,0.72);
  margin-bottom: 2rem;
  font-size: 1.05rem;
}
.kb-cta-banner__actions { display: flex; gap: 1rem; justify-content: center; flex-wrap: wrap; }

@media (max-width: 992px) {
  .kb-blogs-grid { grid-template-columns: repeat(2, 1fr); }
  .kb-blog-full-card--featured { grid-column: span 2; }
}
@media (max-width: 576px) {
  .kb-blogs-grid { grid-template-columns: 1fr; }
  .kb-blog-full-card--featured { grid-column: span 1; grid-template-columns: 1fr; }
  .kb-blog-full-card--featured .kb-blog-full-card__img { min-height: 220px; }
}
</style>
